import { Device } from "@fairfleet/geotab";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utilities/error/getErrorMessage";

type Props = {
  device: Device | null;
  deviceTypeLabel: string;
  isDeviceActive: boolean;
  currentSerialNumber: string;
  canEditSerialNumber: boolean;
  isSerialPending: boolean;
  serialError: unknown;
  isSerialError: boolean;
  onSwap: (serialNumber: string) => void;
  onRemove: () => void;
  isMobile: boolean;
};

export default function AssetSettingsTab({
  device,
  deviceTypeLabel,
  isDeviceActive,
  currentSerialNumber,
  canEditSerialNumber,
  isSerialPending,
  serialError,
  isSerialError,
  onSwap,
  onRemove,
  isMobile,
}: Props) {
  const { t } = useTranslation();
  // The only place a serial number is typed for an existing asset; the page remounts this tab
  // (key) once a swap went through, which clears the field.
  const [newSerialNumber, setNewSerialNumber] = useState("");
  const trimmedSerialNumber = newSerialNumber.trim();
  // Removing the serial number is a database write that detaches the telematics device, so it is confirmed.
  const [isRemoveConfirmOpen, setIsRemoveConfirmOpen] = useState(false);

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{t("assets.deviceSettingsTitle")}</Typography>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1">{t("assets.primaryDeviceTitle")}</Typography>

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {device?.name ?? "-"}
              </Typography>
              <Chip
                size="small"
                color={isDeviceActive ? "success" : "default"}
                label={isDeviceActive ? t("assets.statusActive") : t("assets.statusInactive")}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary">
              {t("assets.deviceTypeLabel")}: {deviceTypeLabel}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t("assets.deviceLabel")}: {device?.id ?? "-"}
            </Typography>

            <TextField
              size="small"
              label={t("assets.serialNumberLabel")}
              value={newSerialNumber}
              onChange={event => setNewSerialNumber(event.target.value)}
              placeholder={t("assets.swapTargetPlaceholder")}
            />

            <Stack direction={isMobile ? "column" : "row"} spacing={1.5}>
              <Button
                variant="contained"
                onClick={() => onSwap(trimmedSerialNumber)}
                disabled={
                  !canEditSerialNumber ||
                  trimmedSerialNumber === "" ||
                  trimmedSerialNumber === currentSerialNumber ||
                  isSerialPending
                }
              >
                {t("assets.swapAction")}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => setIsRemoveConfirmOpen(true)}
                disabled={!canEditSerialNumber || !currentSerialNumber || isSerialPending}
              >
                {t("assets.removeSerialNumber")}
              </Button>
            </Stack>

            {isSerialError && (
              <Alert severity="error">{getErrorMessage(serialError) || t("assets.saveError")}</Alert>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={isRemoveConfirmOpen} onClose={() => setIsRemoveConfirmOpen(false)}>
        <DialogTitle>{t("assets.removeSerialConfirmTitle")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("assets.removeSerialConfirmText", { serialNumber: currentSerialNumber, name: device?.name ?? "" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRemoveConfirmOpen(false)}>{t("assets.cancel")}</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              setIsRemoveConfirmOpen(false);
              onRemove();
            }}
          >
            {t("assets.removeSerialNumber")}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
